import api from './api';

export interface User {
  userID: number;
  username: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  employeeID: number | null;
  personelNo: string | null;
  departmentID: number | null;
  departmentName: string | null;
  positionName: string | null;
  roles: string[];
  lastLoginDate: string | null;
  twoFactorEnabled?: boolean;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUrl: string;
  otpauthUrl: string;
}

export interface LoginRequest {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
  requiresTwoFactor?: boolean;
  challengeToken?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

const TOKEN_KEY = 'pdks_token';
const REFRESH_TOKEN_KEY = 'pdks_refresh_token';
const USER_KEY = 'pdks_user';

const authService = {
  // Giris yap
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    const data = response.data;

    // 2FA required — do not store tokens yet
    if (data.requiresTwoFactor) {
      return data;
    }

    // Token'lari kaydet
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));

    // API header'ina token ekle
    api.setAuthToken(data.token);

    return data;
  },

  // 2FA kodu dogrula
  verifyTwoFactor: async (challengeToken: string, code: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/verify-2fa', { challengeToken, code });
    const data = response.data;

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    api.setAuthToken(data.token);

    return data;
  },

  // Cikis yap
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Hata olsa bile local storage'i temizle
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    api.setAuthToken(null);
  },

  // Token yenile
  refreshToken: async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      const response = await api.post<{ token: string; refreshToken: string }>('/auth/refresh', {
        refreshToken
      });

      localStorage.setItem(TOKEN_KEY, response.data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      api.setAuthToken(response.data.token);

      return response.data.token;
    } catch (error) {
      // Refresh token gecersiz, cikis yap
      authService.logout();
      return null;
    }
  },

  // Mevcut kullaniciyi getir
  getCurrentUser: async (): Promise<User | null> => {
    try {
      const response = await api.get<User>('/auth/me');
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // Local storage'dan kullanici bilgisini al
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  // Token'i al
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Oturum acik mi?
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    // Token'in suresini kontrol et (basit kontrol)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000;
      return Date.now() < expiry;
    } catch {
      return false;
    }
  },

  // Auth durumunu baslat (sayfa yuklendiginde)
  initializeAuth: (): AuthState => {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = authService.getStoredUser();

    if (token && authService.isAuthenticated()) {
      api.setAuthToken(token);
      return {
        isAuthenticated: true,
        user,
        token
      };
    }

    return {
      isAuthenticated: false,
      user: null,
      token: null
    };
  },

  // Kullanicinin rolu var mi?
  hasRole: (role: string): boolean => {
    const user = authService.getStoredUser();
    return user?.roles?.includes(role) ?? false;
  },

  // Kullanicinin rollerinden biri var mi?
  hasAnyRole: (roles: string[]): boolean => {
    const user = authService.getStoredUser();
    return roles.some(role => user?.roles?.includes(role));
  },

  // 2FA: Kullanicinin 2FA durumunu kontrol et (me endpoint'inden)
  checkTwoFactorStatus: async (): Promise<boolean> => {
    try {
      const response = await api.get<User>('/auth/me');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (response as any).data ?? response;
      const enabled = data.twoFactorEnabled === true;
      // Stored user'i guncelle
      const stored = authService.getStoredUser();
      if (stored) {
        stored.twoFactorEnabled = enabled;
        localStorage.setItem(USER_KEY, JSON.stringify(stored));
      }
      return enabled;
    } catch {
      return false;
    }
  },

  // 2FA: Setup baslat - QR code ve secret dondurur
  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    const response = await api.post<TwoFactorSetupResponse>('/auth/setup-2fa');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = (response as any).data ?? response;
    return result;
  },

  // 2FA: Kodu dogrulayip 2FA'yi etkinlestir
  enable2FA: async (code: string): Promise<void> => {
    await api.post('/auth/enable-2fa', { code });
    // Stored user'i guncelle
    const stored = authService.getStoredUser();
    if (stored) {
      stored.twoFactorEnabled = true;
      localStorage.setItem(USER_KEY, JSON.stringify(stored));
    }
  },

  // 2FA: Devre disi birak
  disable2FA: async (code: string): Promise<void> => {
    await api.post('/auth/disable-2fa', { code });
    // Stored user'i guncelle
    const stored = authService.getStoredUser();
    if (stored) {
      stored.twoFactorEnabled = false;
      localStorage.setItem(USER_KEY, JSON.stringify(stored));
    }
  },

  // 2FA setup gerekli mi?
  isTwoFactorSetupRequired: (): boolean => {
    const user = authService.getStoredUser();
    return user ? user.twoFactorEnabled !== true : false;
  }
};

export default authService;
